if (!customElements.get('scroll-appear')) {
  customElements.define('scroll-appear', class MediaLogoScroll extends HTMLElement {
    static observerShared = null;
    static observerTargets = null;

    static getObserver() {
      if (this.observerShared) return this.observerShared;

      this.observerTargets = this.observerTargets || new Map();
      this.observerShared = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const instance = this.observerTargets.get(entry.target);
          instance?.handleViewportEntry?.(entry);
        });
      }, {
        root: null,
        rootMargin: '200px 0px 200px 0px',
        threshold: 0
      });

      return this.observerShared;
    }

    static observeInstance(instance) {
      if (!instance) return;
      this.observerTargets = this.observerTargets || new Map();
      this.observerTargets.set(instance, instance);
      this.getObserver().observe(instance);
    }

    static unobserveInstance(instance) {
      if (!instance || !this.observerShared) return;

      this.observerShared.unobserve(instance);
      this.observerTargets?.delete(instance);

      if (this.observerTargets?.size === 0) {
        this.observerShared.disconnect();
        this.observerShared = null;
        this.observerTargets = null;
      }
    }

    constructor() {
      super();
      this.targetProgress = 0;
      this.currentProgress = 0;
      this.isFinished = false;
      this.animationId = null;
      this.isPortrait = false;
      this.isMobile = false;
      this.isInstantMode = false;
      this.isFooter = false;
      this.isInViewport = false;
      this.scrollRafId = null;
      this.resizeRafId = null;
      this.sectionTop = 0;
      this.sectionHeight = 0;
      this.containerLeft = 0;
      this.containerRight = 0;
      this.isActive = false;
      this.lastSlideTransform = '';
      this.smoothedProgress = 0;
      this.lastScrollTime = 0;
      this.scrollVelocity = 0;
    }

    connectedCallback() {
      this.mode = this.getAttribute('data-mode');
      if (this.mode === 'none') {
        this.style.visibility = 'visible';
        return;
      }

      this.isFooter = this.closest('.footer-group') !== null;

      const rawScale = parseFloat(this.getAttribute('data-scale'));
      this.targetScale = !isNaN(rawScale) ? rawScale * 0.01 : 0.6;
      this.shrinkAmount = 1 - this.targetScale;

      this.direction = this.getAttribute('data-direction') || 'right';

      const defaultPoints = this.mode === 'shrink'
        ? { startPortrait: 0.45, startLandscape: 0.25, endPortrait: 0.9, endLandscape: 0.75 }
        : { startPortrait: 0.1, startLandscape: 0.1, endPortrait: 0.35, endLandscape: 0.55 };

      let customPoints = {};
      const pointsAttr = this.getAttribute('data-scroll-points');
      if (pointsAttr) {
        try {
          customPoints = JSON.parse(pointsAttr);
        } catch (e) {
        }
      }

      this.startPortrait = customPoints.startPortrait ?? defaultPoints.startPortrait;
      this.startLandscape = customPoints.startLandscape ?? defaultPoints.startLandscape;
      this.endPortrait = customPoints.endPortrait ?? defaultPoints.endPortrait;
      this.endLandscape = customPoints.endLandscape ?? defaultPoints.endLandscape;

      if (this.isFooter && this.mode === 'slide') {
        this.endPortrait = 0.6;
        this.endLandscape = 0.6;
      }

      this.animated = this.querySelector('[slot="animated"] > *:not(style)');
      this.container = this.querySelector('[slot="container"]');
      this.wrapper = this.querySelector('[slot="wrapper"]');

      this.style.visibility = 'visible';

      this.parentSection = this.closest('.shopify-section');
      this.isLastSection = this._isLastMainSection();

      this.boundOnScroll = this._onScroll.bind(this);
      this.boundOnResize = this._onResize.bind(this);
      this.boundOnResizeQueued = this._queueResize.bind(this);

      this._initSmoothingParams();

      if (this.isLastSection) {
        this._completeWithoutAnimation();
        return;
      }

      this._setupIntersectionObserver();
    }

    _isLastMainSection() {
      if (!this.parentSection) return false;

      const mainContent = document.getElementById('MainContent');
      const scope = mainContent || document;
      const sections = Array.from(scope.querySelectorAll('.shopify-section')).filter((section) => (
        !section.classList.contains('shopify-section-group-header-group')
                && !section.classList.contains('shopify-section-group-footer-group')
      ));

      return sections.length > 0 && sections[sections.length - 1] === this.parentSection;
    }

    _completeWithoutAnimation() {
      this.isInViewport = true;
      this.isFinished = true;
      this.isActive = false;
      this.currentProgress = this.mode === 'shrink' ? 0 : 1;
      this.targetProgress = this.currentProgress;
      this.smoothedProgress = this.currentProgress;
      this.setAttribute('data-instant', 'true');

      if (this.mode === 'slide' && this.animated) {
        this.animated.style.transform = 'translateX(0px)';
        this.lastSlideTransform = 'translateX(0px)';
      }

      if (this.mode === 'shrink' && this.container) {
        this.container.style.transform = 'scale(1)';
        this.lastShrinkTransform = 'scale(1)';
      }
    }

    _initSmoothingParams() {
      if (this.mode === 'shrink') {
        this.baseLerpFactor = 0.12;
      } else {
        this.baseLerpFactor = 0.08;
      }
      this.velocityLerp = 0.1;
    }

    _setupIntersectionObserver() {
      this.constructor.observeInstance(this);
    }

    handleViewportEntry(entry) {
      if (entry.isIntersecting) {
        this._activateAnimation(entry);
        this.setLazyHold(true);
      } else {
        this._deactivateAnimation();
        this.setLazyHold(false);
      }
    }

    setLazyHold(active) {
      if (!this.parentSection?.classList.contains('section-scrolling-logo')) return;
      if (active === Boolean(this.lazyHeld)) return;

      this.lazyHeld = active;
      const lazySection = customElements.get('lazy-section-template');

      if (active) {
        lazySection?.holdLazyAfter(this.parentSection, 2);
      } else {
        lazySection?.releaseLazyAfter(this.parentSection, 2);
      }
    }

    _activateAnimation(entry) {
      if (this.isInViewport || this.isFinished) return;

      if (this.mode === 'slide' && this._shouldInstantComplete(entry)) {
        this.isInViewport = true;
        this.isFinished = true;
        this.setAttribute('data-instant', 'true');
        if (this.animated) {
          this.animated.style.transform = this.direction === 'right'
            ? 'translateX(100vw)'
            : 'translateX(-100vw)';
          requestAnimationFrame(() => {
            if (!this.isConnected) return;
            this.animated.style.transform = 'translateX(0)';
          });
        }
        return;
      }

      this.isInViewport = true;
      this.isActive = true;
      this.lastScrollTime = performance.now();

      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
      window.addEventListener('resize', this.boundOnResizeQueued);

      this._queueResize();
      requestAnimationFrame(() => {
        if (!this.isActive || !this.isConnected) return;
        this._onScroll();
      });
    }

    _shouldInstantComplete(entry) {
      if (!entry || !entry.boundingClientRect || this.isFooter) return false;
      const rect = entry.boundingClientRect;
      const windowHeight = window.innerHeight;
      return rect.top < windowHeight * 0.9 && rect.bottom > 0;
    }

    _deactivateAnimation() {
      if (!this.isInViewport) return;

      this.isInViewport = false;
      this.isActive = false;

      if (this.mode === 'slide' && !this.isFinished && this.targetProgress >= 0.9999) {
        this.currentProgress = 1;
        this.smoothedProgress = 1;
        this._applyChanges();
        this._finalize();
        return;
      }

      if (this.boundOnScroll) {
        window.removeEventListener('scroll', this.boundOnScroll);
      }
      if (this.boundOnResizeQueued) {
        window.removeEventListener('resize', this.boundOnResizeQueued);
      }

      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
      if (this.scrollRafId) {
        cancelAnimationFrame(this.scrollRafId);
        this.scrollRafId = null;
      }
      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId);
        this.resizeRafId = null;
      }
    }

    _queueResize() {
      if (!this.isActive) return;
      if (this.resizeRafId) return;
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        this._onResize();
      });
    }

    _onResize() {
      const sectionRect = this.getBoundingClientRect();
      this.sectionTop = sectionRect.top + window.scrollY;
      this.sectionHeight = sectionRect.height || this.offsetHeight || 1;

      if (this.container) {
        this.initialHeight = this.container.offsetHeight;
        const containerRect = this.container.getBoundingClientRect();
        this.containerLeft = containerRect.left;
        this.containerRight = containerRect.right;
        if (this.mode === 'shrink') {
          this._applyShrink();
        }
      }
      this.isPortrait = window.innerHeight > window.innerWidth;
      this.isMobileSmall = typeof DeviceUtils !== 'undefined' ? DeviceUtils.isMobileSmall : false;

      this.lerpFactor = this.baseLerpFactor;
    }

    _calculateProgressWithSmoothing(sectionProgress) {
      const startAt = this.isPortrait ? this.startPortrait : this.startLandscape;
      const endAt = this.isPortrait ? this.endPortrait : this.endLandscape;

      let progress = (sectionProgress - startAt) / (endAt - startAt);
      progress = Math.max(0, Math.min(1, progress));

      const progressDiff = progress - this.smoothedProgress;
      this.smoothedProgress += progressDiff * 0.2;

      return this.smoothedProgress;
    }

    _getRawProgress(sectionProgress) {
      const startAt = this.isPortrait ? this.startPortrait : this.startLandscape;
      const endAt = this.isPortrait ? this.endPortrait : this.endLandscape;
      const range = endAt - startAt;
      if (range <= 0) return 0;
      return (sectionProgress - startAt) / range;
    }

    _onScroll() {
      if (this.isFinished && this.mode !== 'shrink') return;
      if (this.scrollRafId) return;

      this.scrollRafId = requestAnimationFrame(() => {
        this.scrollRafId = null;
        const windowHeight = window.innerHeight;
        const viewportTop = this.sectionTop - window.scrollY;

        if (this.isFooter && this.mode === 'slide') {
          const scrollBottom = window.scrollY + windowHeight;
          const docHeight = document.documentElement.scrollHeight;
          if (scrollBottom >= docHeight - 15) {
            this.targetProgress = 1;
            this._startAnimationLoop();
            return;
          }
        }

        const sectionProgress = (windowHeight - viewportTop) / (windowHeight + this.sectionHeight);
        const rawProgress = this._getRawProgress(sectionProgress);

        if (this.mode === 'slide' && rawProgress >= 1) {
          this.targetProgress = 1;
          this.smoothedProgress = 1;
          this._startAnimationLoop();
          return;
        }

        this.targetProgress = this._calculateProgressWithSmoothing(sectionProgress);
        this._startAnimationLoop();
      });
    }

    _startAnimationLoop() {
      if (this.animationId) return;

      const update = () => {
        if (this.isFinished && this.mode !== 'shrink') {
          this.animationId = null;
          return;
        }

        const diff = this.targetProgress - this.currentProgress;
        const threshold = this.mode === 'slide' ? 0.0005 : 0.00001;

        if (Math.abs(diff) < threshold) {
          this.currentProgress = this.targetProgress;
          this._applyChanges();

          if (this.mode === 'slide' && this.targetProgress >= 0.9999) {
            this._finalize();
            return;
          }

          this.animationId = null;
          return;
        }

        this.currentProgress += diff * this.lerpFactor;
        this._applyChanges();
        this.animationId = requestAnimationFrame(update);
      };

      this.animationId = requestAnimationFrame(update);
    }

    _applyChanges() {
      if (this.mode === 'slide') {
        this._applySlide();
      } else if (this.mode === 'shrink') {
        this._applyShrink();
      }
    }

    _applySlide() {
      if (!this.container || !this.animated) return;

      if (this.currentProgress >= 0.9999) {
        if (this.lastSlideTransform !== 'translateX(0px)') {
          this.animated.style.transform = 'translateX(0px)';
          this.lastSlideTransform = 'translateX(0px)';
        }
        return;
      }

      let currentX;
      if (this.direction === 'right') {
        const startX = window.innerWidth - this.containerLeft;
        currentX = (1 - this.currentProgress) * startX;
      } else {
        const startX = this.containerRight;
        currentX = -(1 - this.currentProgress) * startX;
      }

      const nextTransform = `translateX(${currentX.toFixed(2)}px)`;
      if (this.lastSlideTransform === nextTransform) return;

      this.animated.style.transform = nextTransform;
      this.lastSlideTransform = nextTransform;
    }

    _applyShrink() {
      if (!this.container || !this.wrapper) return;

      const scale = 1 - (this.currentProgress * this.shrinkAmount);
      const scaleString = `scale(${scale.toFixed(4)})`;

      if (this.lastShrinkTransform === scaleString) return;

      this.container.style.transform = scaleString;
      this.lastShrinkTransform = scaleString;
    }

    _finalize() {
      this.isFinished = true;
      if (this.mode === 'slide') {
        if (this.animated && this.lastSlideTransform !== 'translateX(0px)') {
          this.animated.style.transform = 'translateX(0px)';
          this.lastSlideTransform = 'translateX(0px)';
        }
      }
      this.isActive = false;
      if (this.boundOnScroll) {
        window.removeEventListener('scroll', this.boundOnScroll);
      }
      if (this.boundOnResizeQueued) {
        window.removeEventListener('resize', this.boundOnResizeQueued);
      }
    }

    disconnectedCallback() {
      this.isFinished = true;
      this.isInViewport = false;
      this.setLazyHold(false);
      this.constructor.unobserveInstance(this);

      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
      if (this.scrollRafId) {
        cancelAnimationFrame(this.scrollRafId);
        this.scrollRafId = null;
      }
      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId);
        this.resizeRafId = null;
      }

      if (this.boundOnScroll) {
        window.removeEventListener('scroll', this.boundOnScroll);
      }
      if (this.boundOnResizeQueued) {
        window.removeEventListener('resize', this.boundOnResizeQueued);
      }
    }
  });
}
